import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatApiErrorDetail } from "../lib/adminApi";
import { adminLogin, getAdminMe } from "../lib/api/index";
import { logger } from "../lib/logger";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    let cancelled = false;
    getAdminMe()
      .then(() => { if (!cancelled) navigate("/admin", { replace: true }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      const dest = location?.state?.from || "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      logger.error("admin_login_failed", { status: err?.response?.status });
      const status = err?.response?.status;
      if (status === 429) {
        const retry = err?.response?.data?.detail?.retry_after_seconds;
        setError(
          `Too many attempts. Try again in ${retry ? Math.ceil(retry / 60) : "a few"} minute(s).`
        );
      } else if (status === 401) {
        setError("Invalid email or password.");
      } else {
        setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="sf-container"
      style={{ padding: "100px 24px", maxWidth: 480 }}
      data-testid="admin-login-page"
    >
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <img src="/systemforge-logo.png" alt="" style={{ width: 56, height: 56, opacity: 0.9 }} />
      </div>
      <div className="sf-eyebrow" style={{ textAlign: "center", marginBottom: 6 }}>SystemForge AI</div>
      <h1
        className="sf-display"
        style={{ fontSize: 32, textAlign: "center", marginBottom: 24, lineHeight: 1.15 }}
        data-testid="admin-login-headline"
      >
        Admin sign-in.
      </h1>

      <form onSubmit={onSubmit} className="sf-card" data-testid="admin-login-form">
        <label htmlFor="admin-email" className="sf-eyebrow" style={{ display: "block", marginBottom: 6 }}>
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="sf-input"
          style={{ width: "100%", marginBottom: 16 }}
          data-testid="admin-login-email"
        />

        <label htmlFor="admin-password" className="sf-eyebrow" style={{ display: "block", marginBottom: 6 }}>
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="sf-input"
          style={{ width: "100%", marginBottom: 18 }}
          data-testid="admin-login-password"
        />

        <button
          type="submit"
          className="sf-btn"
          disabled={submitting || !email || !password}
          style={{ width: "100%" }}
          data-testid="admin-login-submit"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        {error && (
          <div
            className="sf-mono"
            style={{ color: "#e2867e", fontSize: 13, marginTop: 14, textAlign: "center" }}
            data-testid="admin-login-error"
          >
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
